"use client";
import "@/components/home/faq-section.css";
import "@/components/ui/atoms/categories/data-display.css";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/shadcn/accordion";
import { heroSerif } from "@/lib/theme/hero-serif";

import { memo, useState } from "react";

const FAQS = [
    {
        q: "Do I need credit repair experience to run this?",
        a: "No, and that's kind of the point. Fluid has the dispute strategies, the Metro 2® knowledge, and the letter writing built in, so you don't have to spend years learning the industry first. If you can describe what you want in plain English, you can run your business on day one.",
    },
    {
        q: "How does the free trial work?",
        a: "You get 30 days free, and there are no setup fees. After that it's month to month, cancel whenever you like. If we don't keep earning the subscription, we don't deserve it.",
    },
    {
        q: "How hard is it to switch from my current software?",
        a: "About an afternoon, and most of that is the software working, not you. If you're on DisputeFox, DisputeWell migrates the entirety of your data right from the import screen. One button click and we grab literally everything for you: client information, dispute history, documents, monitoring credentials, report and score history, all of it, while you watch. Coming from anywhere else, drop a CSV and map the columns, up to 5,000 rows at a time with duplicate handling built in.",
    },
    {
        q: "Do I still have to print and mail letters myself?",
        a: "No. When you approve a round, the letters print and mail on their own as tracked USPS first-class. Every letter gets a tracking number, and the delivery status reports back into the client's timeline. You never touch an envelope unless you want to.",
    },
    {
        q: "Is Fluid just another AI chatbot?",
        a: "No. A chatbot bolted onto a CRM can only answer questions. Fluid is wired into the actual software, so it pulls reports, picks what to dispute, writes the letters, mails them with tracking, and preps the next round. And everything stops for your approval before it goes out.",
    },
    {
        q: "Will my clients ever see the name DisputeWell?",
        a: "Never. The portal, onboarding, and every email carry your logo, your colors, and your domain. Custom domains set themselves up with a DNS wizard in minutes. And white label is included in the price, not a five-figure add-on.",
    },
    {
        q: "Is my clients' data safe?",
        a: "Yes, and it stays yours. Every business's data is walled off from every other's, credentials and personal info are encrypted, and every sensitive action lands in an audit trail. Your clients can even export or delete their own data right from the portal.",
    },
    {
        q: "Is there a limit on how many clients I can have?",
        a: "Each plan includes a set number of active clients, and you can add more in blocks of 100 whenever you need them. You'll see exactly where you stand on a live usage meter, so you're never cut off by surprise.",
    },
    {
        q: "Can you promise my clients' scores will go up?",
        a: "No, and nobody honest can. What we give you are projections learned from real outcomes, not point promises. What we do promise is the process: cited claims, tracked mail, real escalation between rounds, and receipts for all of it.",
    },
    {
        q: "Is this CROA compliant?",
        a: "The paperwork side is built in. The agreements and disclosures CROA requires, cancellation windows computed in your timezone, a 50-state evidence dashboard, and contract term limits tracked per state. You still have to run an honest business, but we make it easy to prove that you do.",
    },
] as const;

export const FaqSection = memo(function FaqSection() {
    // Every answer stays in the page (for search and find-in-page): the content
    // is force-mounted and the closed ones are hidden.
    const [open, setOpen] = useState("0");

    return (
        <section className={`dw-home__section ${heroSerif.variable}`} aria-label="Letters to the editor" data-component="FaqSection" data-location="src/components/home/faq-section.tsx">
            <div className="dw-home-faq__inner">
                <div className="dw-home-faq__kicker-row">
                    <span className="dw-eyebrow">09 · Letters to the Editor</span>
                    <span className="dw-eyebrow">On the record</span>
                </div>
                <div className="dw-home-faq__lede">
                    <h2 className="dw-home-faq__headline dw-text-hero" data-tone="foreground">The hard questions.</h2>
                    <p className="dw-home-faq__deck dw-text-body" data-tone="muted">
                        These are the questions everyone asks before they switch. Here are our honest answers, in print.
                    </p>
                </div>

                <Accordion type="single" collapsible value={open} onValueChange={setOpen}>
                    {FAQS.map((faq, index) => (
                        <AccordionItem key={faq.q} value={String(index)}>
                            <AccordionTrigger>
                                <span className="dw-text-lead">{faq.q}</span>
                            </AccordionTrigger>
                            <AccordionContent forceMount hidden={open !== String(index)}>
                                <p className="dw-home-faq__a dw-text-body" data-tone="muted">{faq.a}</p>
                            </AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>
            </div>
        </section>
    );
});
